import moment, { Moment } from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './TaxList.module.scss';
import { Period } from '@spike/model';
import Header from '../../components/Header';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useTimeZone, useMarketplace } from 'hooks';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';

import { getTaxListDetailReport } from 'actions/insights/sales/taxListDetailReport/TaxListDetailReportActions';
import { TaxListDetailTable, headerNames } from './TaxListDetailTable';

const toDateParam = (m: Moment) => m.format('YYYY-MM-DD');

interface TaxListProps {
    description?: string;
}

export const TaxList: React.FC<TaxListProps> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();

    const { report, loading } = useSelector((s: RootState) => s.taxListDetailReport);

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return {
            from: now.clone().startOf('day'),
            to: now.clone().endOf('day')
        };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);
    const [allTime, setAllTime] = useState(false);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const calendar = { optionSelected: '2024' };

    const fetchData = (p: Period | null, nextPage: number, nextSize: number) => {
        if (!p) {
            dispatch(getTaxListDetailReport(undefined, undefined, nextPage, nextSize));
            return;
        }
        dispatch(getTaxListDetailReport(toDateParam(p.from), toDateParam(p.to), nextPage, nextSize));
    };

    useEffect(() => {
        fetchData(period, page, size);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangePeriod = (p: Period | null, _allTime: boolean) => {
        setPage(1);
        if (!p) {
            setPeriod(null);
            fetchData(null, 1, size);
            return;
        }
        setPeriod({ from: p.from, to: p.to });
        fetchData({ from: p.from, to: p.to }, 1, size);
    };

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage);
        fetchData(period, nextPage, size);
    };

    const handleSizeChange = (nextSize: number) => {
        setSize(nextSize);
        setPage(1);
        fetchData(period, 1, nextSize);
    };

    const exportFile = useMemo(() => {
        const rows = report?.items ?? [];
        return {
            name: `Tax List - ${marketplace.basics.businessName}.csv`,
            dataInMemory: true,
            headers: headerNames,
            dataToExport: rows.map(r => [
                r.invoiceNumber,
                r.saleDate,
                r.clientName,
                r.category,
                r.type,
                r.item,
                String(r.quantity),
                r.paymentMethod,
                r.taxName,
                `${Number(r.taxRate).toFixed(2)}%`,
                Number(r.netSales).toFixed(2),
                Number(r.taxOnNetSales).toFixed(2)
            ]) as string[][]
        };
    }, [report, marketplace.basics.businessName]);

    return (
        <div className={styles.container}>
            {props.description && <span className={styles.description}>{props.description}</span>}
            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>
            <TaxListDetailTable
                loading={loading}
                data={report && Array.isArray(report.items) ? report.items : null}
            />

            {report && report.items.length > 0 && (
                <div className={styles.paginationWrap}>
                    <Pagination
                        page={report?.meta?.page ?? page}
                        totalPages={report?.meta?.totalPages ?? 1}
                        size={report?.meta?.perPage ?? size}
                        isLoading={loading}
                        onPageChange={handlePageChange}
                        onSizeChange={handleSizeChange}
                    />
                </div>
            )}
        </div>
    );
};

export default TaxList;
